import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { API, getAuthHeader } from '../../App';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Copy, Download, ExternalLink, Loader2 } from 'lucide-react';
import { MergedHistory } from './MergedHistory';

const aed = (n) => Number(n || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const exportExcel = (rows) => {
  const data = [];
  rows.forEach((d) => d.records.forEach((r, i) => data.push({
    'Employee ID': d.employee_unique_id,
    'Names match': d.names_match ? 'Yes' : 'NO - check',
    Record: i + 1,
    'Keep?': r.payroll_rider_id === d.keep_record ? 'KEEP' : 'merge into kept',
    Name: r.name,
    'Emirates ID': r.emirates_id || '-',
    Created: r.created_at,
    Source: r.source,
    'Payroll runs': r.payroll_runs,
    'Contract loan': r.contract_loan,
    'Onboarding expenses': r.expenses,
    'Recovered': r.recovered,
    'Outstanding shown today': r.outstanding,
    'Merged: contract includes expenses': i === 0 ? d.merged_if_contract_includes_expenses : '',
    'Merged: expenses are extra': i === 0 ? d.merged_if_expenses_are_extra : '',
  })));
  const ws = XLSX.utils.json_to_sheet(data);
  ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 18 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Duplicate Accounts');
  XLSX.writeFile(wb, `Duplicate_Employee_Accounts_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

const DupCard = ({ d, onResolved, onUndone, onStale, onOpenLoan }) => {
  const [keepId, setKeepId] = useState(d.keep_record);
  const [mode, setMode] = useState('included');
  const [busy, setBusy] = useState(false);
  const keepRec = d.records.find((r) => r.payroll_rider_id === keepId);

  const resolve = async (action) => {
    const others = d.records.filter((r) => r.payroll_rider_id !== keepId).map((r) => `${r.name} (${r.created_at})`).join(', ');
    const what = action === 'merge'
      ? `MERGE ${others} INTO ${keepRec?.name} (${keepRec?.created_at}).\nLoans, recoveries and payroll history move to the kept record; onboarding expenses are counted once${mode === 'extra' ? ' and ADDED on top of the contract loan' : ' (contract loan already includes them)'}.`
      : `DELETE ${others} and its loan account WITHOUT moving anything to ${keepRec?.name}.`;
    if (!window.confirm(`${d.employee_unique_id}: ${what}\n\nA backup is kept and you can undo. Continue?`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/talent/duplicate-employee-accounts/resolve`,
        { employee_unique_id: d.employee_unique_id, keep_id: keepId, record_ids: d.records.map((r) => r.payroll_rider_id), action, expenses_mode: mode }, { headers: getAuthHeader(), timeout: 60000 });
      const backupId = res.data.backup_id;
      (res.data.skipped_fields || []).forEach((s) => toast.warning(`Not copied to ${keepRec?.name}: ${s}`, { duration: 12000 }));
      toast.success(`${d.employee_unique_id} ${action === 'merge' ? 'merged' : 'cleaned up'} - now one account for ${keepRec?.name}`, {
        duration: 15000,
        action: { label: 'Undo', onClick: async () => {
          try {
            await axios.post(`${API}/talent/duplicate-employee-accounts/undo/${backupId}`, {}, { headers: getAuthHeader() });
            toast.success('Undone'); onUndone();
          } catch (e) { toast.error(`Undo failed: ${e?.response?.data?.detail || 'please try again'}`); }
        } },
      });
      onResolved(d.employee_unique_id, keepRec);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 404) onStale();
      toast.error(typeof detail === 'string' ? detail
        : e?.response ? `Could not ${action} (server error ${e.response.status}). Nothing was changed - please try again or send us this Employee ID: ${d.employee_unique_id}`
        : 'Could not reach the server - check your internet connection and try again.', { duration: 12000 });
    } finally {
      setBusy(false);
    }
  };

  return (
  <div className="rounded-lg border border-slate-200 bg-white" data-testid={`dup-${d.employee_unique_id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
      <div className="font-semibold text-slate-800"><span className="font-mono text-blue-700">{d.employee_unique_id}</span> - {d.records[0]?.name}</div>
      <div className="flex flex-wrap gap-2 text-xs">
        {!d.names_match && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid={`dup-names-differ-${d.employee_unique_id}`}>Names differ - may be different people</Badge>}
        <Badge variant="outline">Shown today: AED {aed(d.outstanding_shown_today)}</Badge>
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">If contract includes expenses: AED {aed(d.merged_if_contract_includes_expenses)}</Badge>
        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">If expenses are extra: AED {aed(d.merged_if_expenses_are_extra)}</Badge>
      </div>
    </div>
    <table className="w-full text-xs">
      <thead className="bg-slate-50 text-slate-500">
        <tr>{['Keep', 'Name', 'Emirates ID', 'Created', 'Payroll runs', 'Contract loan', 'Expenses', 'Recovered', 'Outstanding'].map((h) => <th key={h} className="px-3 py-1.5 text-left font-medium">{h}</th>)}</tr>
      </thead>
      <tbody>
        {d.records.map((r) => {
          const kept = r.payroll_rider_id === keepId;
          return (
            <tr key={r.payroll_rider_id} title="Open this loan account" className={`group border-t border-slate-100 cursor-pointer ${kept ? 'bg-green-50/60 hover:bg-green-100/60' : 'hover:bg-blue-50/60'}`} onClick={() => onOpenLoan(r)} data-testid={`dup-row-${r.payroll_rider_id}`}>
              <td className="px-3 py-1.5" title="Choose which record to keep" onClick={(e) => { e.stopPropagation(); setKeepId(r.payroll_rider_id); }}>
                <input type="radio" checked={kept} onChange={() => setKeepId(r.payroll_rider_id)} className="accent-green-600" data-testid={`dup-keep-${r.payroll_rider_id}`} />
                <span className={`ml-1.5 text-[10px] font-semibold ${kept ? 'text-green-700' : 'text-slate-400'}`}>{kept ? 'KEEP' : 'remove'}</span>
              </td>
              <td className="px-3 py-1.5 text-blue-700 group-hover:underline" data-testid={`dup-open-loan-${r.payroll_rider_id}`}>
                {r.name}<ExternalLink className="inline w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              </td>
              <td className="px-3 py-1.5 font-mono">{r.emirates_id || '-'}</td>
              <td className="px-3 py-1.5">{r.created_at}</td>
              <td className="px-3 py-1.5">{r.payroll_runs}</td>
              <td className="px-3 py-1.5">{aed(r.contract_loan)}</td>
              <td className="px-3 py-1.5">{aed(r.expenses)} <span className="text-slate-400">({r.expense_lines})</span></td>
              <td className="px-3 py-1.5">{aed(r.recovered)}</td>
              <td className="px-3 py-1.5 font-semibold">{aed(r.outstanding)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-4 py-2">
      <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid={`dup-mode-${d.employee_unique_id}`}>
        <option value="included">Contract loan already includes expenses</option>
        <option value="extra">Expenses are owed on top of contract</option>
      </select>
      <Button size="sm" disabled={busy} className="h-8 bg-green-600 text-white hover:bg-green-700" onClick={() => resolve('merge')} data-testid={`dup-merge-${d.employee_unique_id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Merge into kept'}
      </Button>
      <Button size="sm" disabled={busy} variant="outline" className="h-8 border-red-200 text-red-700 hover:bg-red-50" onClick={() => resolve('delete')} data-testid={`dup-delete-${d.employee_unique_id}`}>
        Delete other
      </Button>
    </div>
  </div>
  );
};

export const DuplicateAccountsDialog = ({ onOpenLoan, onChanged, reopenSignal = 0 }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => { if (reopenSignal > 0) load(true); }, [reopenSignal]);

  const openLoan = (r) => { setOpen(false); onOpenLoan?.(r); };
  const refresh = () => { load(true); onChanged?.(); };
  const removeResolved = (uid, keepRec) => {
    setRows((prev) => (prev || []).filter((x) => x.employee_unique_id !== uid));
    onChanged?.();
    if (keepRec) openLoan(keepRec);
  };
  const keepOpenForToast = (e) => { if (e.target?.closest?.('[data-sonner-toaster]')) e.preventDefault(); };

  const undoMerge = async (h) => {
    if (!window.confirm(`Undo the ${h.action} of ${h.employee_unique_id}? Both records and their loan accounts go back to how they were.`)) return;
    try {
      await axios.post(`${API}/talent/duplicate-employee-accounts/undo/${h.backup_id}`, {}, { headers: getAuthHeader() });
      toast.success(`${h.employee_unique_id} undone`); refresh();
    } catch (e) { toast.error(`Undo failed: ${e?.response?.data?.detail || 'please try again'}`); }
  };
  const dismiss = async (h) => {
    if (!window.confirm(`Remove ${h.employee_unique_id} from Merged history? Do this only once you've checked the final statement. (The backup is still kept.)`)) return;
    try {
      await axios.post(`${API}/talent/duplicate-employee-accounts/history/${h.backup_id}/dismiss`, {}, { headers: getAuthHeader() });
      setHistory((prev) => prev.filter((x) => x.backup_id !== h.backup_id));
      toast.success(`${h.employee_unique_id} removed from Merged history`);
    } catch (e) { toast.error(`Could not remove: ${e?.response?.data?.detail || 'please try again'}`); }
  };

  const load = async (silent = false) => {
    setOpen(true);
    if (!(silent === true && rows)) setLoading(true);
    try {
      const res = await axios.get(`${API}/talent/duplicate-employee-accounts`, { headers: getAuthHeader(), timeout: 120000 });
      setRows(res.data.duplicates || []);
      setHistory(res.data.history || []);
    } catch (e) {
      toast.error(`Could not load duplicates (${e?.response?.status || 'network'})`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={load} data-testid="duplicate-accounts-btn">
        <Copy className="w-4 h-4" />Duplicate Accounts
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl max-h-[88vh] overflow-y-auto" onPointerDownOutside={keepOpenForToast} onInteractOutside={keepOpenForToast} data-testid="duplicate-accounts-dialog">
          <DialogHeader>
            <DialogTitle>Duplicate Employee Accounts {rows ? `(${rows.length})` : ''}</DialogTitle>
            <DialogDescription>
              Employees that appear twice with the same Employee ID. Tick KEEP on the record to keep, then Merge (moves loans,
              recoveries and payroll history into it) or Delete the other. Click a name to open its loan account - closing it brings you back here.
              Every change is backed up and can be undone.
            </DialogDescription>
          </DialogHeader>
          {loading && <div className="flex items-center gap-2 py-10 justify-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Checking all employees...</div>}
          {!loading && (
            <MergedHistory history={history} onUndo={undoMerge} onDismiss={dismiss}
              onView={(h) => openLoan({ payroll_rider_id: h.keep_id, name: h.kept_name })} />
          )}
          {!loading && rows && rows.length === 0 && <div className="py-10 text-center text-slate-500">No duplicate accounts found.</div>}
          {!loading && rows && rows.length > 0 && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" className="gap-2" onClick={() => exportExcel(rows)} data-testid="duplicate-accounts-excel"><Download className="w-4 h-4" />Download Excel</Button>
              </div>
              {rows.map((d) => <DupCard key={d.employee_unique_id} d={d} onResolved={removeResolved} onUndone={refresh} onStale={refresh} onOpenLoan={openLoan} />)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
